import React, { useRef, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import ProductCard from './ProductCard';
import { Button } from './ui/button';

const ProductSlider = ({ products, title, subtitle, onAddToCart, viewAllLink = '/products' }) => {
  const sliderRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScrollButtons = () => {
    if (sliderRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = sliderRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScrollButtons();
    const slider = sliderRef.current;
    if (slider) {
      slider.addEventListener('scroll', checkScrollButtons);
      window.addEventListener('resize', checkScrollButtons);
    }
    return () => {
      if (slider) {
        slider.removeEventListener('scroll', checkScrollButtons);
      }
      window.removeEventListener('resize', checkScrollButtons);
    };
  }, [products]);

  const scroll = (direction) => {
    if (sliderRef.current) {
      const cardWidth = 280; // Approximate card width including gap
      const scrollAmount = direction === 'left' ? -cardWidth * 2 : cardWidth * 2;
      sliderRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (!products || products.length === 0) {
    return null;
  }

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-white" data-testid="product-slider-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 sm:mb-8 lg:mb-10">
          <div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-gray-900">
              {title?.split(' ').map((word, i) => (
                <span key={i} className={i === title.split(' ').length - 1 ? 'text-purple-600' : ''}>
                  {word}{' '}
                </span>
              ))}
            </h2>
            {subtitle && (
              <p className="text-sm sm:text-base lg:text-lg text-gray-500 mt-2">{subtitle}</p>
            )}
          </div>
          
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Navigation Arrows */}
            <div className="hidden sm:flex items-center gap-2">
              <button
                onClick={() => scroll('left')}
                disabled={!canScrollLeft}
                className={`p-2 sm:p-3 rounded-full border-2 transition-all duration-200 ${
                  canScrollLeft
                    ? 'border-purple-600 text-purple-600 hover:bg-purple-600 hover:text-white'
                    : 'border-gray-200 text-gray-300 cursor-not-allowed'
                }`}
                aria-label="Scroll left"
              >
                <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
              <button
                onClick={() => scroll('right')}
                disabled={!canScrollRight}
                className={`p-2 sm:p-3 rounded-full border-2 transition-all duration-200 ${
                  canScrollRight
                    ? 'border-purple-600 text-purple-600 hover:bg-purple-600 hover:text-white'
                    : 'border-gray-200 text-gray-300 cursor-not-allowed'
                }`}
                aria-label="Scroll right"
              >
                <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>
            
            {/* View All Link */}
            <Link to={viewAllLink}>
              <Button 
                variant="outline" 
                className="border-purple-600 text-purple-600 hover:bg-purple-600 hover:text-white text-sm sm:text-base"
              >
                View All
                <ArrowRight className="ml-1 sm:ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>

        {/* Product Slider */}
        <div className="relative">
          {/* Left Gradient Overlay */}
          {canScrollLeft && (
            <div className="absolute left-0 top-0 bottom-0 w-8 sm:w-16 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
          )}
          
          {/* Right Gradient Overlay */}
          {canScrollRight && (
            <div className="absolute right-0 top-0 bottom-0 w-8 sm:w-16 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />
          )}

          {/* Slider Container */}
          <div
            ref={sliderRef}
            className="flex gap-4 sm:gap-6 overflow-x-auto scrollbar-hide scroll-smooth pb-4"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch'
            }}
          >
            {products.map((product, index) => (
              <div
                key={product.id}
                className="flex-shrink-0 w-[160px] sm:w-[200px] md:w-[240px] lg:w-[260px]"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <ProductCard
                  product={product}
                  onAddToCart={onAddToCart}
                />
              </div>
            ))}
          </div>

          {/* Mobile Navigation Dots */}
          <div className="flex sm:hidden justify-center gap-2 mt-4">
            <button
              onClick={() => scroll('left')}
              disabled={!canScrollLeft}
              className={`p-2 rounded-full ${
                canScrollLeft ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-400'
              }`}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => scroll('right')}
              disabled={!canScrollRight}
              className={`p-2 rounded-full ${
                canScrollRight ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-400'
              }`}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductSlider;